<script setup lang="ts">
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH, RIGHT_PANEL_WIDTH } from '~/composables/useAppLayout'
import { useDarkMode } from '~/composables/useDarkMode'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import 'moment/dist/locale/da'

const { isSidebarExpanded, isRightPanelOpen } = useAppLayout()
const chromeStore = useChromeStore()
const { isDark } = useDarkMode()

// Prevent scroll-behind when UI is blocked. Uses body overflow rather than
// a non-passive wheel listener so the browser can keep its scroll optimisations.
watch(
  () => chromeStore.blockUI,
  (blocked) => {
    if (import.meta.client) {
      document.body.style.overflow = blocked ? 'hidden' : ''
    }
  },
  { immediate: true },
)
onUnmounted(() => { if (import.meta.client) document.body.style.overflow = '' })
const i18n = useI18n()

const localePrefs = usePreferencesNamespace('preferenceNamespaceLocale')
const currentLocale = computed(() => localePrefs.result.value?.locale ?? 'da')

watch(
  currentLocale,
  (v) => {
    if (v) {
      i18n.locale.value = v
      const momentLocale = v === 'da-DK' ? 'da' : v
      moment.locale(momentLocale)
    }
  },
  { immediate: true },
)

const hasIntegrationPermission = useHasPermissions(PERMISSIONS.INTEGRATION_READ).result

watch(
  hasIntegrationPermission,
  async (val) => {
    if (val) {
      try {
        const menuWarnings = await useSideMenuWarnings()
        useMenuStore().setWarnings(menuWarnings.warnings)
      } catch {}
    }
  },
  { immediate: true },
)

useHead({
  link: [{
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=block',
  }],
})

const marginLeft  = computed(() => isSidebarExpanded.value ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH)
const marginRight = computed(() => isRightPanelOpen.value  ? RIGHT_PANEL_WIDTH      : 0)

const handleKeydown = (event: KeyboardEvent) => {
  if (event.altKey && event.code === 'KeyF') {
    event.preventDefault()
    chromeStore.toggleSearch()
  }
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault()
    chromeStore.toggleSearch()
  }
}

onMounted(() => window.addEventListener('keydown', handleKeydown))
onUnmounted(() => window.removeEventListener('keydown', handleKeydown))
</script>

<template>
  <div class="h-screen bg-background overflow-hidden" :class="{ dark: isDark }">

    <!-- Left sidebar -->
    <AppSidebar />

    <!-- Search modal -->
    <AppSearchModal />

    <!-- Right panel (always in DOM for teleport target) -->
    <AppRightPanel />

    <!-- Portal targets for old @align/ui modals (ChromeModal) -->
    <div class="fixed inset-0 z-[9999] pointer-events-none flex items-start justify-center pt-32">
      <div class="relative w-full h-[calc(100%-4rem)] flex flex-col items-center pointer-events-none *:pointer-events-auto">
        <portal-target name="default" />
      </div>
    </div>
    <div
      class="fixed top-0 left-0 w-screen z-[999999999] pointer-events-none flex justify-center items-center"
      :class="chromeStore.blockUI ? 'h-screen' : 'h-0'"
    >
      <div class="relative grid h-full w-full pointer-events-none">
        <div
          v-for="modal in chromeStore.registeredModals"
          :key="modal"
          class="[grid-row:1] [grid-column:1] flex flex-col items-center pointer-events-none pt-20 max-h-[calc(100vh-5rem)] overflow-y-auto *:pointer-events-auto"
        >
          <portal-target :name="`chrome-notification-layer-${modal}`" />
        </div>
        </div>
    </div>
    <div
      v-if="chromeStore.blockUI"
      class="fixed inset-0 z-[999999998] bg-black/50"
      style="touch-action: none;"
    />

    <!-- Content area — shifts in sync with sidebar and panel -->
    <div
      class="h-screen flex flex-col transition-[margin] duration-300 ease-in-out"
      :style="{ marginLeft: `${marginLeft}rem`, marginRight: `${marginRight}rem` }"
    >
      <!-- Fixed topbar -->
      <AppTopbar />

      <!-- Scrollable page content -->
      <main :key="currentLocale" class="flex-1 overflow-y-auto bg-background">
        <slot />
      </main>
    </div>
  </div>
</template>
